import { connectRange } from "instantsearch.js/es/connectors/index.js";
import noUiSlider from "nouislider";
import "nouislider/dist/nouislider.css";
import { getPrecision, normalizeIdentifier } from "../utils.js";

const normalizePrecision = (precision, min, max) => {
  if (Number.isFinite(precision)) {
    return Math.max(0, Math.round(precision));
  }

  return getPrecision(min, max);
};

const roundToPrecision = (value, precision, method = "round") => {
  const factor = Math.pow(10, precision);
  return Number(
    (Math[method](Number(value) * factor) / factor).toFixed(precision)
  );
};

const clamp = (value, min, max) => {
  return Math.min(Math.max(value, min), max);
};

const getSliderStart = (start, min, max, precision) => {
  const [startMin, startMax] = Array.isArray(start) ? start : [];
  const numericStartMin = Number(startMin);
  const numericStartMax = Number(startMax);

  return [
    Number.isFinite(numericStartMin)
      ? clamp(roundToPrecision(numericStartMin, precision), min, max)
      : min,
    Number.isFinite(numericStartMax)
      ? clamp(roundToPrecision(numericStartMax, precision), min, max)
      : max,
  ];
};

const formatValue = (value, precision) => {
  return value === undefined ? "" : Number(value).toFixed(precision);
};

export const chamfrRangeSlider = connectRange(
  (renderOptions, isFirstRender) => {
    const {
      start,
      range: { min, max },
      refine,
      widgetParams,
    } = renderOptions;

    const { container, attribute } = widgetParams;
    const id = normalizeIdentifier(attribute);
    const rawMin = Number(min);
    const rawMax = Number(max);
    const filterContainer = container.closest(".filter-box");

    if (
      !Number.isFinite(rawMin) ||
      !Number.isFinite(rawMax) ||
      rawMin === rawMax
    ) {
      if (filterContainer) {
        filterContainer.style.display = "none";
      }
      return;
    }

    if (filterContainer) {
      filterContainer.style.display = "";
    }

    const precision = normalizePrecision(widgetParams.precision, rawMin, rawMax);
    const factor = Math.pow(10, precision);
    const step = Number((1 / factor).toFixed(precision));
    const sliderMin = roundToPrecision(rawMin, precision, "floor");
    const sliderMax = roundToPrecision(rawMax, precision, "ceil");
    const sliderStart = getSliderStart(start, sliderMin, sliderMax, precision);
    const existingSlider = container.querySelector(`.${id}-slider`);

    if (isFirstRender || !existingSlider?.noUiSlider) {
      container.innerHTML = "";

      // Create inputs
      const inputWrapper = document.createElement("div");
      inputWrapper.className = "range-inputs";
      inputWrapper.innerHTML = `
		<div class="range-from">
			<label for="${id}-min">From:</label>
			<input type="number" id="${id}-min" step="${step}">
		</div>
		<div class="range-to">
			<label for="${id}-max">To:</label>
			<input type="number" id="${id}-max" step="${step}">
		</div>
      `;
      container.appendChild(inputWrapper);

      const minInput = inputWrapper.querySelector(`#${id}-min`);
      const maxInput = inputWrapper.querySelector(`#${id}-max`);

      // Create slider div
      const slider = document.createElement("div");
      slider.className = `${id}-slider`;
      container.appendChild(slider);

      // Initialize noUiSlider
      noUiSlider.create(slider, {
        start: sliderStart,
        connect: true,
        range: { min: sliderMin, max: sliderMax },
        step: step,
        tooltips: false,
        format: {
          to: (value) => formatValue(value, precision),
          from: (value) => Number(value),
        },
      });

      // Slider -> Inputs
      slider.noUiSlider.on("update", (values) => {
        minInput.value = values[0];
        maxInput.value = values[1];
      });

      // Slider -> Algolia
      slider.noUiSlider.on("change", (values) => {
        refine([Number(values[0]), Number(values[1])]);
      });

      // Inputs -> Slider + Algolia
      const syncFromInputs = () => {
        const newMin = Number(minInput.value);
        const newMax = Number(maxInput.value);
        if (!isNaN(newMin) && !isNaN(newMax)) {
          const refinedStart = [
            clamp(roundToPrecision(newMin, precision), sliderMin, sliderMax),
            clamp(roundToPrecision(newMax, precision), sliderMin, sliderMax),
          ];
          slider.noUiSlider.set(refinedStart);
          refine(refinedStart);
        }
      };

      minInput.addEventListener("change", syncFromInputs);
      maxInput.addEventListener("change", syncFromInputs);
    } else {
      const slider = existingSlider;
      const minInput = widgetParams.container.querySelector(`#${id}-min`);
      const maxInput = widgetParams.container.querySelector(`#${id}-max`);

      if (minInput) {
        minInput.step = step;
      }

      if (maxInput) {
        maxInput.step = step;
      }

      slider.noUiSlider.updateOptions({
        start: sliderStart,
        range: { min: sliderMin, max: sliderMax },
        step: step,
        format: {
          to: (value) => formatValue(value, precision),
          from: (value) => Number(value),
        },
      });
      slider.noUiSlider.set(sliderStart);
    }
  }
);
